import { useEffect, useRef, useState } from 'react';

const easeOutCubic = (progress: number) => 1 - (1 - progress) ** 3;

export type UseAnimatedNumberOptions = {
  duration?: number;
};

export const useAnimatedNumber = (target: number, options: UseAnimatedNumberOptions = {}) => {
  const { duration = 400 } = options;
  const [displayValue, setDisplayValue] = useState(() => (Number.isFinite(target) ? target : 0));
  const displayRef = useRef(Number.isFinite(target) ? target : 0);
  const frameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const to = Number.isFinite(target) ? target : 0;
    const from = displayRef.current;

    if (from === to) {
      return;
    }

    const start = performance.now();
    if (frameRef.current !== undefined) {
      cancelAnimationFrame(frameRef.current);
    }

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      const next = from + (to - from) * easeOutCubic(progress);
      displayRef.current = next;
      setDisplayValue(next);

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      displayRef.current = to;
      setDisplayValue(to);
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current !== undefined) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [duration, target]);

  return displayValue;
};
